import { ComponentType } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@digdir/designsystemet-react';
import {
  BarChartIcon,
  PencilWritingIcon,
  PersonGroupIcon,
  ShieldLockIcon,
} from '@navikt/aksel-icons';

import { Team } from '../../api/queries';
import { Routes } from '../../enums/Routes';
import { useSelectedUnitContext } from '../../contexts/selectedUnitContext';
import { SurveyFormCardUtils } from '../../utils/SurveyFormCardUtils/SurveyFormCard.utils';
import { scoreStatusOf } from '../../domain/psychological-safety/team-result';
import { getInitials } from '../../utils/getInitials';
import { VarianceIndicator } from '../VarianceIndicator';

import classes from './TeamCard.module.css';

export type TeamCardProps = Team & {
  isTeamEditable?: boolean;
};

type ModuleRow = {
  key: string;
  name: string;
  icon: ComponentType;
  description: string;
  score?: { value: string; max: number; tone: string };
  standardDeviation?: number;
};

const scoreFormat = new Intl.NumberFormat('nb-NO', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * The same data the card has always shown: psychological safety status and
 * score, and custom survey counts when there are any.
 */
const useModuleRows = (team: Team): ModuleRow[] => {
  const { t } = useTranslation();
  const rows: ModuleRow[] = [];

  // A max score of 0 means the team has no measured score yet
  const status =
    team.maxPossibleScore > 0
      ? scoreStatusOf(team.averageScore)
      : 'zero';
  const hasScore = status !== 'zero';
  const statusLabels: Record<string, string> = {
    success: t('psychologicalSafety.status.greenLight'),
    warning: t('psychologicalSafety.status.yellowLight'),
    danger: t('psychologicalSafety.status.redLight'),
    zero: t('psychologicalSafety.status.noMeasurements'),
  };

  rows.push({
    key: 'psychologicalSafety',
    name: t('surveys.page.modules.psychologicalSafety'),
    icon: ShieldLockIcon,
    description: statusLabels[status] ?? t('psychologicalSafety.status.unknown'),
    score: hasScore
      ? { value: scoreFormat.format(team.averageScore), max: team.maxPossibleScore, tone: status }
      : undefined,
    standardDeviation:
      hasScore && team.standardDeviation !== undefined && team.standardDeviation !== null
        ? team.standardDeviation
        : undefined,
  });

  const completed = team.completedCustomSurveysCount ?? 0;
  const active = team.activeCustomSurveysCount ?? 0;
  if (completed + active > 0) {
    rows.push({
      key: 'custom',
      name: t('surveys.page.modules.custom'),
      icon: BarChartIcon,
      description: [
        completed > 0 && t('teams.card.customCompleted', { count: completed }),
        active > 0 && t('teams.card.customActive', { count: active }),
      ]
        .filter(Boolean)
        .join(' · '),
    });
  }

  return rows;
};

const TeamCard = ({ isTeamEditable, ...team }: TeamCardProps) => {
  const { t } = useTranslation();
  const rows = useModuleRows(team);

  return (
    <li className={classes.card}>
      <div className={classes.header}>
        <span aria-hidden="true" className={classes.avatar}>
          {getInitials(team.name)}
        </span>
        <div className={classes.titleBlock}>
          <h2 className={classes.name}>{team.name}</h2>
          <span className={classes.members}>
            <PersonGroupIcon aria-hidden="true" />
            {t('teams.card.members', { count: team.numberOfTeamMembers })}
          </span>
        </div>
      </div>

      <div className={classes.body}>
        <ul className={classes.modules}>
          {rows.map(({ key, name, icon: Icon, description, score, standardDeviation }) => (
            <li key={key} className={classes.module}>
              <span aria-hidden="true" className={classes.moduleIcon}>
                <Icon />
              </span>
              <span className={classes.moduleText}>
                <span className={classes.moduleName}>{name}</span>
                <span className={classes.subtle}>{description}</span>
                {standardDeviation !== undefined && (
                  <span className={classes.agreement}>
                    <span className={classes.subtle}>{t('teams.variance.label')}</span>
                    <VarianceIndicator
                      standardDeviation={standardDeviation}
                      showExplanation={false}
                      showLabel={false}
                    />
                  </span>
                )}
              </span>
              {score && (
                <span className={classes.score} data-tone={score.tone}>
                  <span aria-hidden="true" className={classes.dot} />
                  <span className={classes.scoreValue}>{score.value}</span>
                  <span className={classes.subtle}>
                    {t('teams.card.scoreOf', { max: score.max })}
                  </span>
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {isTeamEditable && <TeamActions teamId={team.id} teamName={team.name} />}
    </li>
  );
};

type TeamActionsProps = {
  teamId: number;
  teamName: string;
};

const TeamActions = ({ teamId, teamName }: TeamActionsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setSelectedTeamId } = useSelectedUnitContext();

  const navigateToTeamDashboard = () => {
    setSelectedTeamId(teamId);
    navigate(Routes.Dashboard);
  };

  const navigateToSurveyCreation = () => {
    SurveyFormCardUtils.removeAllSessionKeys();
    setSelectedTeamId(teamId);
    navigate(Routes.CreateSurvey);
  };

  return (
    <div className={classes.footer}>
      {/* The page's only primary action is "Lag nytt team", so the card actions stay quiet */}
      <Button
        variant="secondary"
        data-size="sm"
        onClick={navigateToTeamDashboard}
        aria-label={t('teams.card.viewOverviewAriaLabel', { teamName })}
      >
        {t('teams.card.viewOverviewButton')}
      </Button>
      <Button
        variant="tertiary"
        data-size="sm"
        onClick={navigateToSurveyCreation}
        aria-label={t('teams.card.createSurveyAriaLabel', { teamName })}
      >
        {t('teams.card.createSurveyButton')}
      </Button>
      <Button asChild variant="tertiary" data-size="sm">
        <RouterLink to={`${teamId}/setup`} aria-label={t('teams.card.editAriaLabel', { teamName })}>
          <PencilWritingIcon aria-hidden="true" />
          {t('teams.card.edit')}
        </RouterLink>
      </Button>
    </div>
  );
};

export default TeamCard;
